import { useCallback, useEffect, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import { hasDirtyOpenEntityEditor, markAllEntityEditorsClean } from '../lib/entityEditorRegistry'

interface PageNavigationGuard {
  /** Whether the shared "you have unsaved changes" dialog should be shown. */
  dialogOpen: boolean
  /** Discards (page-level and entity-editor) unsaved state and proceeds with the navigation. */
  confirmLeave: () => void
  /** Cancels the navigation attempt, leaving all in-progress edits intact. */
  cancelLeave: () => void
}

/**
 * Consolidated in-app navigation guard for pages that own page-level dirty
 * state (AgentDetail, GlobalSettings, MambuMcpDetail). Intercepts every
 * react-router transition away from the current route — left-nav clicks,
 * breadcrumb links, and the TopBar brand/logo click all call `navigate()`
 * under the hood, so a single `useBlocker` here covers all of them.
 *
 * Blocks whenever the page itself is dirty (page-level `useDirty` state,
 * passed in as `pageDirty`) OR any open entity editor (Skill/Connection/
 * Knowledge Base source editor) has unsaved changes — entity scope is
 * consulted first in the sense that either source alone is sufficient to
 * trigger the same single generic prompt; there is never more than one
 * dialog shown for a given navigation attempt.
 *
 * `clearPageDirty` (typically `useDirty(scope).clearDirty`) is called on
 * Discard, before `blocker.proceed()`. The page usually unmounts right after —
 * which alone would clear the scope via `useDirty`'s own cleanup effect — but
 * clearing it explicitly here means the page is never dirty for the instant
 * between confirming Discard and the navigation actually landing.
 *
 * Tab-bearing detail pages (Agent, Global Settings, MCP…) now encode their
 * active tab as the trailing path segment (`/agents/:slug/:tab`). A tab
 * switch is therefore also a pathname change, which would otherwise fire
 * this blocker *in addition to* the page's own `pendingTab`/dialog
 * machinery — two dialogs for one click. `isSamePageShell` treats a
 * same-entity tab switch as staying on the same page, leaving the
 * page-level dialog in sole charge; cross-page navigation (a different
 * agent id/slug, a different MCP product, or a shorter/longer path) still
 * blocks here as before.
 *
 * Matches against each tab-bearing route's actual pattern (leading params
 * before `:tab`), not raw segment-count arithmetic — a positional
 * "same length, same prefix" comparison would also treat `/agents/123` →
 * `/agents/456` as a tab switch, which is wrong: those are two different
 * agents, not two tabs of the same one.
 */
const TAB_BEARING_ROUTES: { section: string; leadingParamCount: number }[] = [
  { section: 'agents', leadingParamCount: 1 }, // /agents/:id/:tab
  { section: 'mcp', leadingParamCount: 1 }, // /mcp/:product/:tab
  { section: 'mcp-analytics', leadingParamCount: 1 }, // /mcp-analytics/:product/:tab
  { section: 'global-settings', leadingParamCount: 0 }, // /global-settings/:tab
]

function isSamePageShell(currentPathname: string, nextPathname: string): boolean {
  const current = currentPathname.split('/').filter(Boolean)
  const next = nextPathname.split('/').filter(Boolean)
  const route = TAB_BEARING_ROUTES.find(r => r.section === current[0])
  if (!route) return false
  const expectedLength = route.leadingParamCount + 2 // section + leading params + :tab
  if (current.length !== expectedLength || next.length !== expectedLength) return false
  if (next[0] !== route.section) return false
  for (let i = 1; i <= route.leadingParamCount; i++) {
    if (current[i] !== next[i]) return false
  }
  return true
}

export function usePageNavigationGuard(pageDirty: boolean, clearPageDirty?: () => void): PageNavigationGuard {
  const shouldBlock = useCallback(
    // `hasDirtyOpenEntityEditor()` reads a module-level registry, not a dep —
    // safe because `useBlocker` re-evaluates `shouldBlock` fresh on every
    // navigation attempt, so this always reads the registry's current value
    // rather than one captured at callback-creation time.
    ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) =>
      (pageDirty || hasDirtyOpenEntityEditor()) &&
      currentLocation.pathname !== nextLocation.pathname &&
      !isSamePageShell(currentLocation.pathname, nextLocation.pathname),
    [pageDirty]
  )
  const blocker = useBlocker(shouldBlock)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (blocker.state === 'blocked') setDialogOpen(true)
  }, [blocker.state])

  const confirmLeave = useCallback(() => {
    setDialogOpen(false)
    markAllEntityEditorsClean()
    clearPageDirty?.()
    blocker.proceed?.()
  }, [blocker, clearPageDirty])

  const cancelLeave = useCallback(() => {
    setDialogOpen(false)
    blocker.reset?.()
  }, [blocker])

  return { dialogOpen, confirmLeave, cancelLeave }
}

/**
 * Single, app-wide `beforeunload` guard — replaces the three previously
 * duplicated per-page listeners (AgentDetail, GlobalSettings, MambuMcpDetail).
 * Mounted once (in TopBar, which is always rendered while authenticated) so it
 * guards refresh/tab-close regardless of which page is currently active.
 *
 * Consults both the page-level `useDirty` aggregate (`anyDirty`, passed in)
 * and the entity-editor registry.
 *
 * The prototype has no equivalent (agent-studio.html:11688, bug B13), but
 * dropping the guard here would silently discard unsaved Agent / Global
 * Settings / Mambu MCP edits on refresh or tab-close — a data-loss regression
 * the prototype is not a reason to ship. In-app navigation is covered
 * separately by `usePageNavigationGuard`.
 */
export function useBeforeUnloadGuard(anyDirty: boolean): void {
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (anyDirty || hasDirtyOpenEntityEditor()) e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [anyDirty])
}
